import React, { useEffect, useState, useRef } from "react";
import Layout1 from "../../DataLayouts/Layout1";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { fetchEmployeeExit } from "../../../Apis/Employee-api";
import { Info } from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import {
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "intern_exit_record_id",
  sortOrder: "desc",
};

const INTERN_EXIT_TOOLBAR_FILTERS = [
  { urlKey: "clearance_status", field: "clearance_status" },
  { urlKey: "exit_type", field: "exit_type" },
];

const CLEARANCE_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
];

const EXIT_TYPE_OPTIONS = [
  { value: "Completed", label: "Completed" },
  { value: "Terminated", label: "Terminated" },
  { value: "Resigned", label: "Resigned" },
  { value: "ConvertedToEmployee", label: "Converted To Employee" },
];

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (key.endsWith("_operator") || key.endsWith("_logic") || !key.startsWith("filter_")) return;
    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);
    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;
      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }
      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({ field: "intern_name", operator: "contains", value: search });
  }

  return filters;
};

const DEFAULT_COLUMNS = [
  { field: "intern_name", label: "Intern Name", visible: true, width: 180, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "intern_code", label: "Intern Code", visible: true, width: 130, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "exit_type", label: "Exit Type", visible: true, width: 150, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "exit_date", label: "Exit Date", visible: true, width: 140, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "last_working_day", label: "Last Working Date", visible: true, width: 160, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "certificate_issued", label: "Certificate Issued", visible: true, width: 150, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "certificate_issue_date", label: "Certification Issue Date", visible: true, width: 180, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "handover_completed", label: "Handover Completed", visible: true, width: 160, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "handover_notes", label: "Handover Notes", visible: false, width: 180, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "manager_feedback", label: "Manager Feedback", visible: false, width: 180, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "intern_feedback", label: "Intern Feedback", visible: false, width: 180, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "clearance_status", label: "Clearance Status", visible: true, width: 150, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "exit_reason", label: "Exit Reason", visible: true, width: 160, filterable: true, sortable: true, pinned: "none", required: false },
];

function InterExitRecordList() {
  const [exit, setexit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `intern_exit_records_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL defaults once
  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([k, v]) => {
      if (!params.has(k)) {
        params.set(k, v);
        needsUpdate = true;
      }
    });
    if (needsUpdate) setSearchParams(params, { replace: true });
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load data via metadata filtered endpoint
  useEffect(() => {
    if (!hasInitializedURL.current || !org?.organization_id) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const requestBody = {
          filters: mergeToolbarFilters(
            buildFiltersFromParams(searchParams),
            searchParams,
            INTERN_EXIT_TOOLBAR_FILTERS
          ),
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "intern_exit_record_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/intern-exit-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const rows = Array.isArray(result.data) ? result.data : [];
        const mapped = rows.map((item) => {
          const recordId = Number(item?.intern_exit_record_id ?? item?.id);
          return {
          ...item,
          intern_exit_record_id: Number.isInteger(recordId) ? recordId : item?.intern_exit_record_id,
          id: Number.isInteger(recordId) ? recordId : item?.id,
          intern_name: item?.intern_name || "",
          exit_date: formatDate(item?.exit_date),
          last_working_day: formatDate(item?.last_working_day),
          notice_period_start: formatDate(item?.notice_period_start),
          notice_period_end: formatDate(item?.notice_period_end),
          certificate_issue_date: formatDate(item?.certificate_issue_date),
          handover_completed: item?.handover_completed == 1 ? "✔" : "✖",
          certificate_issued: item?.certificate_issued == 1 ? "✔" : "✖",
        };
        });

        setexit(mapped);
        const p = result?.pagination || {};
        const pageSize = p.pageSize || p.limit || 10;
        setPaginationData({
          page: p.page || requestBody.page,
          pageSize,
          total: p.total || 0,
          totalPages: p.totalPages || 1,
          hasNext: p.hasNext || false,
          hasPrev: p.hasPrev || false,
        });
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error fetching intern exits:", error);
        toast.error("Failed to load intern exits");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => fetchControllerRef.current?.abort();
  }, [searchParams, org?.organization_id]);

  const getRecordId = (itemOrId) => {
    if (itemOrId && typeof itemOrId === "object") {
      const recordId = Number(itemOrId.intern_exit_record_id ?? itemOrId.id);
      return Number.isInteger(recordId) && recordId > 0 ? recordId : null;
    }
    const recordId = Number(itemOrId);
    return Number.isInteger(recordId) && recordId > 0 ? recordId : null;
  };

  let deleteExit = async (id) => {
    const recordId = getRecordId(id);
    if (!recordId) {
      toast.error("Invalid intern exit record.");
      return;
    }
    try {
      const org_id = org.organization_id;
      const response = await axios.delete(
        `${MAIN_URL}/api/organizations/${org_id}/intern-exit/${recordId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

       if (response.status === 200) {
        toast.success(response.data.message);
           } else {
        const errorMessage =
          response.data.message ||
          response.data.errors?.[Object.keys(response.data.errors)[0]]?.[0] ||
          "Failed to delete Intern Record";

        toast.error(errorMessage);
        console.warn("Deletion error:", response.status, response.data);
      }

    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Delete failed:", error);
      toast.error(
        error.response?.data?.errors?.intern_exit_record_id?.[0] ||
          error.response?.data?.error ||
          "Failed to delete Intern Exit Details"
      );
    }
  };

  const navigate = useNavigate();

  const handleEdit = useCallback(
    (item) => {
      const recordId = getRecordId(item);
      if (!recordId) {
        toast.error("Invalid intern exit record.");
        return;
      }
      navigate(`/organization/intern/intern-exit/edit/${recordId}`);
    },
    [navigate]
  );


               const handleShow = useCallback(
    (item) => {
      const recordId = getRecordId(item);
      if (!recordId) {
        toast.error("Invalid intern exit record.");
        return;
      }
      navigate(`/organization/intern/intern-exit/view/${recordId}`);
    },
    [navigate],
  )


  return (
    <>
      <Layout4
        loading={loading}
        delete_action={"INTERN_EXIT_DELETE"}
        heading={"Intern  Exit Record"}
        btnName={"Add Record"}
        add_action={"INTERN_EXIT_ADD"}
        Data={exit}
        tableHeaders={[
          { name: "Code", value_key: "employee_code", width: "50px" },
          { name: "Name", value_key: "name", width: "150px" },

          {
            name: "NoticePeriod Starts",
            value_key: "notice_period_start",
            width: "170px",
          },

          {
            name: "NoticePeriod Ends",
            value_key: "notice_period_end",
            width: "170px",
          },
          {
            name: "Resignation Date",
            value_key: "resignation_date",
            width: "150px",
          },
          {
            name: "Relieving Date",
            value_key: "relieving_date",
            width: "150px",
          },
          {
            name: "Interview",
            value_key: "exit_interview_done",
            width: "50px",
          },
        ]}
        Icons={[
          <AutorenewIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <AlarmAddIcon sx={{ color: "text.secondary" }} />,
          <LogoutIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Intern exit details",
          "Intern exit details",
          "Add Intern Exit Details",
          "Intern Exit Deatils",
        ]}
        Route={"/organization/intern/intern-exit"}
        setData={setexit}
        DeleteFunc={deleteExit}
      />

      <TableDataGeneric
        tableName="intern_exit_records"
        primaryKey="intern_exit_record_id"
        heading="Intern Exit"
        data={exit}
        paginationData={paginationData}
        sortname={"intern_name"}
        showActions={true}
        // apiUrl={`${MAIN_URL}/api/organizations/${org?.organization_id}/employee-exit`}
        Route="/organization/intern/intern-exit"
        DeleteFunc={deleteExit}
        EditFunc={handleEdit}
        edit_delete_action={["INTERN_EXIT_EDIT", "INTERN_EXIT_DELETE"]}
        filterModule="intern_exit_records"
        toolbarExtras={
          <>
            <ToolbarFilterSelect
              label="Clearance Status"
              urlKey="clearance_status"
              allLabel="All clearance statuses"
              options={CLEARANCE_STATUS_OPTIONS}
              valueKey="value"
              labelKey="label"
              searchParams={searchParams}
              onChange={handleToolbarFilterChange}
            />
            <ToolbarFilterSelect
              label="Exit Type"
              urlKey="exit_type"
              allLabel="All exit types"
              options={EXIT_TYPE_OPTIONS}
              valueKey="value"
              labelKey="label"
              searchParams={searchParams}
              onChange={handleToolbarFilterChange}
            />
          </>
        }
        handleShow={handleShow}
        token={localStorage.getItem("token")}
        configss={configColumns}
        {...(tableConfig && { config: tableConfig })}
      />
    </>
  );
}

export default InterExitRecordList;
